import type {
  DartInput,
  DartThrow,
  GameConfig,
  GameState,
  MatchMode,
  PlayerState,
  TeamDraft,
  TeamState,
  TurnRecord,
} from "./types";

const TEAM_COLORS = ["#f3bc46", "#f25f5c", "#4cc9b0", "#8e7dff", "#39a8ff", "#ff83c6"];
const PLAYER_COLORS = ["#ffd56a", "#ff7c78", "#6de8cf", "#aa9eff", "#67c5ff", "#ff9bd4"];

export const makeId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function normalizedDrafts(drafts: TeamDraft[], mode: MatchMode): TeamDraft[] {
  return drafts.map((draft, teamIndex) => ({
    name:
      draft.name.trim() ||
      (mode === "individual" ? draft.players[0]?.trim() : "") ||
      `Squadra ${teamIndex + 1}`,
    players: draft.players.map((name, playerIndex) =>
      name.trim() || `Player ${playerIndex + 1}`,
    ),
  }));
}

export function createGame(
  drafts: TeamDraft[],
  config: GameConfig,
  mode: MatchMode = "individual",
  now = Date.now(),
): GameState {
  const teams: TeamState[] = [];
  const players: PlayerState[] = [];
  let playerColorIndex = 0;

  normalizedDrafts(drafts, mode).forEach((draft, teamIndex) => {
    const teamId = makeId();
    const teamColor = TEAM_COLORS[teamIndex % TEAM_COLORS.length];
    const members = draft.players.map((name) => {
      const player: PlayerState = {
        id: makeId(),
        teamId,
        name,
        color: PLAYER_COLORS[playerColorIndex++ % PLAYER_COLORS.length],
      };
      players.push(player);
      return player.id;
    });
    teams.push({
      id: teamId,
      name: mode === "individual" ? draft.players[0] : draft.name,
      score: config.startScore,
      legs: 0,
      color: teamColor,
      playerIds: members,
    });
  });

  const firstTeam = teams[0];
  const firstPlayerId = firstTeam.playerIds[0];
  const activeMemberByTeam = Object.fromEntries(teams.map((team) => [team.id, 0]));
  activeMemberByTeam[firstTeam.id] = firstTeam.playerIds.length > 1 ? 1 : 0;

  return {
    version: 2,
    mode,
    config,
    teams,
    players,
    activeTeam: 0,
    startingTeam: 0,
    activeMemberByTeam,
    activePlayerId: firstPlayerId,
    leg: 1,
    round: 1,
    currentTurnStart: config.startScore,
    currentTurn: [],
    turns: [],
    status: "playing",
    createdAt: now,
    updatedAt: now,
  };
}

export function activeTeam(state: GameState): TeamState {
  return state.teams[state.activeTeam];
}

export function activePlayer(state: GameState): PlayerState {
  return state.players.find((player) => player.id === state.activePlayerId) ?? state.players[0];
}

function selectMember(
  teams: TeamState[],
  teamIndex: number,
  cursors: Record<string, number>,
): { playerId: string; cursors: Record<string, number> } {
  const team = teams[teamIndex];
  const memberIndex = cursors[team.id] ?? 0;
  const playerId = team.playerIds[memberIndex] ?? team.playerIds[0];
  return {
    playerId,
    cursors: {
      ...cursors,
      [team.id]: team.playerIds.length > 1 ? (memberIndex + 1) % team.playerIds.length : 0,
    },
  };
}

function nextTurn(state: GameState, turn: TurnRecord, now: number): GameState {
  const nextTeam = (state.activeTeam + 1) % state.teams.length;
  const selection = selectMember(state.teams, nextTeam, state.activeMemberByTeam);
  const nextRound = nextTeam === state.startingTeam ? state.round + 1 : state.round;
  return {
    ...state,
    activeTeam: nextTeam,
    activeMemberByTeam: selection.cursors,
    activePlayerId: selection.playerId,
    round: nextRound,
    currentTurnStart: state.teams[nextTeam].score,
    currentTurn: [],
    turns: [...state.turns, turn],
    updatedAt: now,
  };
}

function makeTurn(
  state: GameState,
  darts: DartThrow[],
  score: number,
  bust: boolean,
  checkout: boolean,
): TurnRecord {
  return {
    id: makeId(),
    teamId: activeTeam(state).id,
    playerId: state.activePlayerId,
    leg: state.leg,
    round: state.round,
    darts,
    score,
    bust,
    checkout,
  };
}

export function throwDart(
  state: GameState,
  input: DartInput,
  now = Date.now(),
): GameState {
  if (state.status !== "playing" || state.currentTurn.length >= 3) return state;

  const team = activeTeam(state);
  const player = activePlayer(state);
  const value = input.segment * input.multiplier;
  const dart: DartThrow = {
    ...input,
    id: makeId(),
    value,
    teamId: team.id,
    playerId: player.id,
    leg: state.leg,
    round: state.round,
    dartIndex: state.currentTurn.length,
    timestamp: now,
  };
  const darts = [...state.currentTurn, dart];
  const remaining = team.score - value;
  const validDouble = input.multiplier === 2;
  const bust =
    remaining < 0 ||
    (state.config.doubleOut && remaining === 1) ||
    (remaining === 0 && state.config.doubleOut && !validDouble);

  if (bust) {
    const turn = makeTurn(state, darts, 0, true, false);
    const restoredTeams = state.teams.map((item, index) =>
      index === state.activeTeam ? { ...item, score: state.currentTurnStart } : item,
    );
    const moved = nextTurn({ ...state, teams: restoredTeams }, turn, now);
    return {
      ...moved,
      notice: {
        id: makeId(),
        type: "bust",
        title: "BUST",
        detail: `${player.name} torna a ${state.currentTurnStart}`,
      },
    };
  }

  const teams = state.teams.map((item, index) =>
    index === state.activeTeam ? { ...item, score: remaining } : item,
  );

  if (remaining === 0) {
    const turn = makeTurn(state, darts, state.currentTurnStart, false, true);
    const wonLegs = team.legs + 1;
    const wonMatch = wonLegs >= state.config.legsToWin;
    const winningTeams = teams.map((item, index) =>
      index === state.activeTeam ? { ...item, legs: wonLegs } : item,
    );

    if (wonMatch) {
      return {
        ...state,
        teams: winningTeams,
        currentTurn: [],
        turns: [...state.turns, turn],
        status: "finished",
        winnerTeamId: team.id,
        checkoutPlayerId: player.id,
        updatedAt: now,
        notice: {
          id: makeId(),
          type: "match",
          title: "MATCH!",
          detail: `${team.name} vince ${wonLegs} leg`,
        },
      };
    }

    const nextStarter = (state.startingTeam + 1) % state.teams.length;
    const resetTeams = winningTeams.map((item) => ({
      ...item,
      score: state.config.startScore,
    }));
    const selection = selectMember(resetTeams, nextStarter, state.activeMemberByTeam);
    return {
      ...state,
      teams: resetTeams,
      activeTeam: nextStarter,
      startingTeam: nextStarter,
      activeMemberByTeam: selection.cursors,
      activePlayerId: selection.playerId,
      leg: state.leg + 1,
      round: 1,
      currentTurnStart: state.config.startScore,
      currentTurn: [],
      turns: [...state.turns, turn],
      checkoutPlayerId: player.id,
      updatedAt: now,
      notice: {
        id: makeId(),
        type: "leg",
        title: "LEG!",
        detail: `${player.name} chiude per ${team.name}`,
      },
    };
  }

  const partial: GameState = {
    ...state,
    teams,
    currentTurn: darts,
    updatedAt: now,
    notice: {
      id: makeId(),
      type: "hit",
      title: input.label,
      detail: `${remaining} rimanenti`,
    },
  };

  if (darts.length < 3) return partial;

  const turnScore = state.currentTurnStart - remaining;
  const turn = makeTurn(state, darts, turnScore, false, false);
  return {
    ...nextTurn(partial, turn, now),
    notice: {
      id: makeId(),
      type: "turn",
      title: String(turnScore),
      detail: `${player.name} · turno completato`,
    },
  };
}

export function endTurn(state: GameState, now = Date.now()): GameState {
  if (state.status !== "playing" || state.currentTurn.length === 0) return state;
  let next = state;
  while (next.activePlayerId === state.activePlayerId && next.currentTurn.length > 0) {
    next = throwDart(next, { segment: 0, multiplier: 0, label: "MISS" }, now);
  }
  return next;
}

export function rematch(state: GameState, now = Date.now()): GameState {
  const drafts = state.teams.map((team) => ({
    name: team.name,
    players: team.playerIds.map(
      (playerId) => state.players.find((player) => player.id === playerId)?.name ?? "Player",
    ),
  }));
  return createGame(drafts, state.config, state.mode, now);
}

export function allThrows(state: GameState): DartThrow[] {
  return [...state.turns.flatMap((turn) => turn.darts), ...state.currentTurn];
}
